import { redactUrlTokens } from './urlTokens';
/**
 * Sentry's browser SDK records the page URL on every event (`request.url`), on navigation and
 * fetch/XHR breadcrumbs, and on transaction spans. Several of this app's URLs are bearer
 * credentials: a share link is `/study/:id?token=shr_…`, a report download is
 * `/report-download/rpt_…`, and password-reset and invitation tokens are path segments. Left alone
 * they land in Sentry storage, where anyone with project access could replay them.
 *
 * Every string anywhere in an event, transaction or breadcrumb goes through the same redaction
 * the access logs use (lib/urlTokens.js), rather than a list of fields that would need extending
 * each time the SDK records a URL somewhere new. The patterns anchor on `?`/`&` parameter names,
 * `shr_`/`rpt_` prefixes and the 43-char token path segment, so ordinary text passes unchanged.
 */
/** Deeper than any event Sentry builds; the cap only guards against a self-referencing payload. */
const MAX_DEPTH = 12;
function scrubValue(value, depth, seen) {
    if (typeof value === 'string')
        return redactUrlTokens(value);
    if (value === null || typeof value !== 'object' || depth > MAX_DEPTH)
        return value;
    if (seen.has(value))
        return value;
    seen.add(value);
    const record = value;
    for (const key of Object.keys(record)) {
        const current = record[key];
        writeBack(record, key, current, key === 'query_string'
            ? scrubQueryString(current, depth + 1, seen)
            : scrubValue(current, depth + 1, seen));
    }
    return value;
}
/**
 * Stores a scrubbed value only when scrubbing changed it. Payloads can carry library objects with
 * getter-only or frozen properties, and a plain assignment to one throws, which made Sentry drop
 * the whole event. A getter that does yield a token is replaced by the redacted value; if the
 * property cannot be redefined either, the throw stands so the event is dropped, not sent with it.
 */
function writeBack(record, key, current, next) {
    if (next === current || Reflect.set(record, key, next))
        return;
    Object.defineProperty(record, key, {
        value: next,
        writable: true,
        enumerable: true,
        configurable: true,
    });
}
/**
 * `request.query_string` is the query WITHOUT its leading `?` (as a string, or as an object or
 * pair list), so a `token=…` parameter has no delimiter for the URL rules to anchor on.
 */
function scrubQueryString(value, depth, seen) {
    if (typeof value === 'string')
        return redactUrlTokens(`?${value}`).slice(1);
    if (Array.isArray(value)) {
        return value.map((pair) => Array.isArray(pair) && typeof pair[0] === 'string' && typeof pair[1] === 'string'
            ? [pair[0], redactUrlTokens(`?${pair[0]}=${pair[1]}`).slice(pair[0].length + 2)]
            : scrubValue(pair, depth, seen));
    }
    if (value && typeof value === 'object') {
        const record = value;
        for (const key of Object.keys(record)) {
            const v = record[key];
            if (typeof v === 'string')
                writeBack(record, key, v, redactUrlTokens(`?${key}=${v}`).slice(key.length + 2));
        }
    }
    return value;
}
/** Scrubs, in place, an error event, a transaction event or a breadcrumb; returns it. */
export function scrubSentryPayload(payload) {
    return scrubValue(payload, 0, new WeakSet());
}
